import {
  Grid,
  TableContainer,
  Typography,
  TableHead,
  TableCell,
  TableBody,
  TableRow,
  Table,
  IconButton,
  Card,
  Box,
  Container,
  Avatar,
  Chip,
  Tooltip,
  Fade,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Divider,
  Stack,
  OutlinedInput,
  ListItemText,
  Checkbox,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { getAllStaff, updateStaff, archiveStaff } from "../api/crud";
import { optionsBranch } from "../data/branch-data";
import { matchOptions } from "../utils/function";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import SchoolIcon from "@mui/icons-material/School";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import PersonIcon from "@mui/icons-material/Person";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import BadgeIcon from "@mui/icons-material/Badge";
import WarningIcon from "@mui/icons-material/Warning";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  border: '1px solid rgba(74, 144, 226, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.15)',
    borderColor: 'rgba(74, 144, 226, 0.3)',
  }
}));

const GradientHeader = styled(Box)(({ theme }) => ({
  borderRadius: '16px 16px 0 0',
  padding: theme.spacing(3),
  color: 'white',
  position: 'relative',
  overflow: 'hidden',
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  '& .MuiTableHead-root': {
    background: 'linear-gradient(90deg, #f8f9fa 0%, #e9ecef 100%)',
  },
  '& .MuiTableCell-head': {
    fontWeight: 600,
    color: '#495057',
    borderBottom: '2px solid #dee2e6',
  },
  '& .MuiTableRow-root': {
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(74, 144, 226, 0.04)',
      transform: 'scale(1.001)',
    }
  }
}));

const ActionComponent = ({ id, staff, onEdit, onArchive, onUnarchive }) => {
  const profile = useSelector((state) => state.profileReducer.profile);
  const isArchived = staff.role === "-1" || staff.role === -1;

  return (
    <>
      {profile && (profile.role === "0" || profile.id === id) && (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="แก้ไขข้อมูล" arrow>
            <IconButton 
              onClick={() => onEdit(staff)}
              sx={{
                backgroundColor: 'rgba(74, 144, 226, 0.1)',
                color: '#4a90e2',
                '&:hover': {
                  backgroundColor: 'rgba(74, 144, 226, 0.2)',
                  transform: 'scale(1.1)',
                },
                transition: 'all 0.2s ease'
              }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          
          {profile.role === "0" && (
            isArchived ? (
              <Tooltip title="เรียกคืนสถานะเจ้าหน้าที่" arrow>
                <IconButton 
                  onClick={() => onUnarchive(staff)}
                  sx={{
                    backgroundColor: 'rgba(76, 175, 80, 0.1)',
                    color: '#4caf50',
                    '&:hover': {
                      backgroundColor: 'rgba(76, 175, 80, 0.2)',
                      transform: 'scale(1.1)',
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <UnarchiveIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="เก็บเข้าคลังเจ้าหน้าที่" arrow>
                <IconButton 
                  onClick={() => onArchive(staff)}
                  sx={{
                    backgroundColor: 'rgba(255, 152, 0, 0.1)',
                    color: '#ff9800',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 152, 0, 0.2)',
                      transform: 'scale(1.1)',
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <ArchiveIcon />
                </IconButton>
              </Tooltip>
            )
          )}
        </Box>
      )}
    </>
  );
};

const Staff = () => {
  const profile = useSelector((state) => state.profileReducer.profile);
  const [adminList, setAdminList] = useState([]);
  const [supervisorList, setSupervisorList] = useState([]);
  const [supervisorBranchList, setSupervisorBranchList] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(() => {
    // Load saved branch filter from localStorage
    return localStorage.getItem('staff-branch-filter') || "";
  }); // For supervisor branch filter
  const [allSupervisors, setAllSupervisors] = useState([]); // Store all supervisors for filtering
  const [archivedStaffList, setArchivedStaffList] = useState([]); // Store archived staff (role = -1)
  
  // Edit Modal States - แยก state เพื่อป้องกัน re-render
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [editRole, setEditRole] = useState('');
  const [editMainBranch, setEditMainBranch] = useState('');
  const [selectedBranches, setSelectedBranches] = useState([]); // For multi-select branches
  
  // Add Staff Modal States
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addRole, setAddRole] = useState('1');
  const [addMainBranch, setAddMainBranch] = useState('1');
  const [addSelectedBranches, setAddSelectedBranches] = useState(['1']);
  
  // Use refs for form inputs to prevent re-renders
  const nameRef = useRef(null);
  const emailRef = useRef(null);
  const telRef = useRef(null);
  const sfIdRef = useRef(null);
  const picUrlRef = useRef(null);
  const addNameRef = useRef(null);
  const addEmailRef = useRef(null);
  const addTelRef = useRef(null);
  const addSfIdRef = useRef(null);
  const addPicUrlRef = useRef(null);

  // State for ID validation
  const [editIdHasLeadingSpace, setEditIdHasLeadingSpace] = useState(false);
  const [addIdHasLeadingSpace, setAddIdHasLeadingSpace] = useState(false);

  // Archive Staff Handlers - moved to the beginning to avoid initialization errors
  const handleArchiveStaff = useCallback(async (staff) => {
    const confirmArchive = window.confirm(
      `คุณต้องการเก็บเข้าคลัง "${staff.sf_name || staff.name}" หรือไม่?\n\nเจ้าหน้าที่ที่ถูกเก็บเข้าคลังจะไม่สามารถเข้าใช้งานระบบได้ แต่ข้อมูลจะยังคงอยู่สำหรับการอ้างอิง`
    );
    
    if (confirmArchive) {
      try {
        await archiveStaff(staff.id);
        // Refresh staff data after archiving
        await getData();
        alert('เก็บเข้าคลังเจ้าหน้าที่สำเร็จ');
      } catch (error) {
        console.error('Error archiving staff:', error);
        alert('เกิดข้อผิดพลาดในการเก็บเข้าคลังเจ้าหน้าที่');
      }
    }
  }, []);

  const handleUnarchiveStaff = useCallback(async (staff) => {
    const confirmUnarchive = window.confirm(
      `คุณต้องการเรียกคืนสถานะของ "${staff.sf_name || staff.name}" หรือไม่?\n\nเจ้าหน้าที่จะสามารถเข้าใช้งานระบบได้อีกครั้ง`
    );
    
    if (confirmUnarchive) {
      try {
        // Update staff to set role back to their previous active role (default to '1' for regular staff)
        const updatedStaff = {
          id: staff.id,
          sf_id: staff.id,
          sf_name: staff.sf_name || staff.name,
          email: staff.email,
          tel: staff.tel,
          role: '1', // You might want to store the previous role somewhere to restore it properly
          b_id: staff.b_id,
          set_b_id: staff.set_b_id,
          pic_url: staff.pic_url
        };
        await updateStaff(updatedStaff);
        // Refresh staff data after unarchiving
        await getData();
        alert('เรียกคืนสถานะเจ้าหน้าที่สำเร็จ');
      } catch (error) {
        console.error('Error unarchiving staff:', error);
        alert('เกิดข้อผิดพลาดในการเรียกคืนสถานะเจ้าหน้าที่');
      }
    }
  }, []);

  // Helper function to check if ID has leading spaces
  const checkIdLeadingSpace = (value) => {
    return value && value !== value.trimStart();
  };

  // Handle ID change for edit modal
  const handleEditIdChange = (event) => {
    const value = event.target.value;
    setEditIdHasLeadingSpace(checkIdLeadingSpace(value));
  };

  // Handle ID change for add modal
  const handleAddIdChange = (event) => {
    const value = event.target.value;
    setAddIdHasLeadingSpace(checkIdLeadingSpace(value));
  };

  // Function to parse set_b_id from string format [x,x] to array of values
  const parseSetBId = (setBIdString) => {
    if (!setBIdString) return [];
    try {
      // Remove brackets and split by comma
      const cleanString = setBIdString.replace(/[[\]]/g, '');
      return cleanString.split(',').map(id => id.trim()).filter(id => id);
    } catch (error) {
      return [];
    }
  };

  // Function to get additional branch names for tooltip
  const getAdditionalBranchNames = (setBIdString, mainBId) => {
    const setBIds = parseSetBId(setBIdString);
    // Filter out duplicates with main branch
    const uniqueSetBIds = setBIds.filter(id => String(id) !== String(mainBId));
    const branchNames = uniqueSetBIds.map(id => matchOptions(optionsBranch, id)).filter(name => name);
    return branchNames.length > 0 ? branchNames.join(', ') : '';
  };

  // Function to get branch color based on branch ID
  const getBranchColor = (branchId) => {
    const colors = {
      1: { bg: '#e8f5e8', color: '#2e7d32' }, // วิศวกรรมอุตสาหการ - เขียว
      2: { bg: '#fff3e0', color: '#f57c00' }, // วิศวกรรมเคมี - ส้ม  
      3: { bg: '#e3f2fd', color: '#1976d2' }, // วิศวกรรมโยธา - น้ำเงิน
      4: { bg: '#fff8e1', color: '#f9a825' }, // วิศวกรรมไฟฟ้า - เหลือง
      5: { bg: '#fce4ec', color: '#c2185b' }, // วิศวกรรมเครื่องกล - ชมพู
      6: { bg: '#e0f2f1', color: '#00796b' }, // วิศวกรรมสิ่งแวดล้อม - เขียวอมฟ้า
      7: { bg: '#f3e5f5', color: '#7b1fa2' }, // วิศวกรรมระบบสมองกลฝังตัวฯ - ม่วง
      8: { bg: '#efebe9', color: '#5d4037' }, // วิศวกรรมวัสดุ - น้ำตาล
    };
    return colors[branchId] || { bg: '#f5f5f5', color: '#616161' }; // Default gray
  };



  const getData = useCallback(async () => {
    const response = await getAllStaff();

    /* 
     * โครงสร้างสิทธิ์การเข้าถึงและอนุมัติ:
     * 
     * role = 0, b_id = 0   -> ฝ่ายพัฒนานิสิต (อนุมัติได้ทุกคน)
     * role = 0, b_id = 1-7 -> อ.ผู้ประสานงานของสาขา (อนุมัติได้เฉพาะสาขาตาม b_id)
     * role = 1, b_id = 1-7 -> คณาจารย์นิเทศ (ไม่มีสิทธิ์อนุมัติคำร้อง)
     * role = 2, b_id = 1-7 -> คณาจารย์นิเทศ + สิทธิ์อนุมัติคำร้อง (อนุมัติคำร้องได้ตาม b_id)
     */

    // ฝ่ายพัฒนานิสิต (role=0, b_id=0) - อนุมัติได้ทุกคน (ยกเว้นที่เก็บเข้าคลัง)
    const studentDevelopment = response.filter((staff) => 
      staff?.role === "0" && staff?.b_id === "0" && staff?.role !== "-1" && staff?.role !== -1
    );

    // อ.ผู้ประสานงานของสาขา (role=0, b_id=1-7) - อนุมัติได้เฉพาะสาขาตาม b_id (เรียงตามสาขา) (ยกเว้นที่เก็บเข้าคลัง)
    const branchCoordinators = response.filter((staff) => {
      const isArchived = staff?.role === "-1" || staff?.role === -1;
      if (isArchived) return false;
      
      return profile?.role === "0" && profile?.b_id === "0"
        ? staff?.role === "0" && staff?.b_id !== "0" // ฝ่ายพัฒนาฯ เห็นได้ทุกสาขา
        : staff?.b_id === profile?.b_id && staff?.role === "0" && staff?.b_id !== "0";
    }).sort((a, b) => parseInt(a.b_id) - parseInt(b.b_id)); // เรียงตามสาขา

    // คณาจารย์นิเทศทั้งหมด (role=1 และ role=2) - รวมผู้ไม่มีสิทธิ์อนุมัติและผู้มีสิทธิ์อนุมัติ (ยกเว้นที่เก็บเข้าคลัง)
    const facultySupervisors = response.filter((staff) => {
      const isArchived = staff?.role === "-1" || staff?.role === -1;
      if (isArchived) return false;
      
      return profile?.role === "0" && profile?.b_id === "0"
        ? (staff?.role === "1" || staff?.role === "2") // ฝ่ายพัฒนาฯ เห็นได้ทุกสาขา
        : staff?.b_id === profile?.b_id && (staff?.role === "1" || staff?.role === "2");
    });

    // เจ้าหน้าที่ที่ถูกเก็บเข้าคลัง (role=-1) - เฉพาะ admin เท่านั้นที่เห็นได้
    const archivedStaff = response.filter((staff) =>
      profile?.role === "0" && (staff?.role === "-1" || staff?.role === -1)
    );

    setAdminList(studentDevelopment);
    setSupervisorBranchList(branchCoordinators);
    setAllSupervisors(facultySupervisors);
    setSupervisorList(facultySupervisors); // Initially show all supervisors
    setArchivedStaffList(archivedStaff);
  }, [profile?.role, profile?.b_id]);

  useEffect(() => {
    getData();
  }, [getData]);

  // Apply saved branch filter when data is loaded
  useEffect(() => {
    if (allSupervisors.length > 0 && selectedBranch) {
      const filteredSupervisors = allSupervisors.filter(supervisor => 
        String(supervisor.b_id) === String(selectedBranch)
      );
      setSupervisorList(filteredSupervisors);
    } else if (allSupervisors.length > 0 && selectedBranch === "") {
      setSupervisorList(allSupervisors);
    }
  }, [allSupervisors, selectedBranch]);

  // Handle branch filter change for supervisors
  const handleBranchFilterChange = (event) => {
    const branchId = event.target.value;
    setSelectedBranch(branchId);
    
    // Save selected branch to localStorage
    if (branchId === "") {
      localStorage.removeItem('staff-branch-filter');
    } else {
      localStorage.setItem('staff-branch-filter', branchId);
    }
    
    if (branchId === "") {
      // Show all supervisors
      setSupervisorList(allSupervisors);
    } else {
      // Filter by selected branch - convert both to string for comparison
      const filteredSupervisors = allSupervisors.filter(supervisor => 
        String(supervisor.b_id) === String(branchId)
      );
      setSupervisorList(filteredSupervisors);
    }
  };

  // Handle edit modal functions
  const handleEditStaff = useCallback((staff) => {
    const role = staff.role || '';
    const mainBranch = staff.b_id || '';
    
    // Initialize selected branches for multi-select
    const currentBranches = parseSetBId(staff.set_b_id || '');
    const initialBranches = [...currentBranches];
    if (mainBranch && !initialBranches.includes(String(mainBranch))) {
      initialBranches.unshift(String(mainBranch));
    }
    
    // Check if initial ID has leading spaces
    const initialIdHasLeadingSpace = checkIdLeadingSpace(staff.id || '');
    
    // Set state for role and branch data
    setEditingStaff(staff);
    setEditRole(role);
    setEditMainBranch(mainBranch);
    setSelectedBranches(initialBranches);
    setEditIdHasLeadingSpace(initialIdHasLeadingSpace);
    setEditModalOpen(true);
  }, []);

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditingStaff(null);
    setEditRole('');
    setEditMainBranch('');
    setSelectedBranches([]);
    setEditIdHasLeadingSpace(false); // Reset validation state
  };

  // Add Staff Modal Handlers
  const handleOpenAddModal = () => {
    setAddRole('1');
    setAddMainBranch('1');
    setAddSelectedBranches(['1']);
    setAddModalOpen(true);
  };
  const handleCloseAddModal = () => {
    setAddModalOpen(false);
    setAddRole('1');
    setAddMainBranch('1');
    setAddSelectedBranches(['1']);
    setAddIdHasLeadingSpace(false); // Reset validation state
  };

  // Add Staff Modal Handlers
  const handleAddRoleChange = useCallback((e) => {
    setAddRole(e.target.value);
  }, []);

  const handleAddMainBranchChange = useCallback((e) => {
    const value = e.target.value;
    setAddMainBranch(value);
    setAddSelectedBranches(prev => {
      const newSelected = value === '0'
        ? prev.filter(id => id !== value)
        : [String(value), ...prev.filter(id => id !== value)];
      return newSelected;
    });
  }, []);

  const handleAddBranchSelectionChange = useCallback((event) => {
    const value = event.target.value;
    setAddSelectedBranches(typeof value === 'string' ? value.split(',') : value);
  }, []);

  // Handle multi-select branch changes with useCallback
  const handleBranchSelectionChange = useCallback((event) => {
    const value = event.target.value;
    const newSelectedBranches = typeof value === 'string' ? value.split(',') : value;
    setSelectedBranches(newSelectedBranches);
  }, []);

  // Optimized event handlers with useCallback to prevent re-renders
  const handleRoleChange = useCallback((e) => {
    const value = e.target.value;
    setEditRole(value);
  }, []);

  const handleMainBranchChange = useCallback((e) => {
    const value = e.target.value;
    setEditMainBranch(value);
    
    // Update selectedBranches to include new main branch without affecting form data
    setSelectedBranches(prevBranches => {
      const newSelectedBranches = value === '0' 
        ? prevBranches.filter(id => id !== value)
        : [String(value), ...prevBranches.filter(id => id !== value)];
      return newSelectedBranches;
    });
  }, []);

  // Convert selected branches array to JSON string format
  const convertBranchesToJson = useCallback((branches) => {
    if (!branches || branches.length === 0) return '';
    
    // Special case: if main branch is ฝ่ายพัฒนานิสิต (b_id=0), don't send set_b_id
    if (editMainBranch === "0" || editMainBranch === 0) return '';
    
    // For other roles: include ALL selected branches (including main branch) in set_b_id
    const allBranches = branches.filter(id => id && String(id) !== "0"); // Filter out ฝ่ายพัฒนานิสิต only
    
    // ถ้ามีแต่สาขาหลัก (selectedBranches มีแค่ 1 ค่า และตรงกับ editMainBranch) ไม่ต้องส่ง set_b_id
    if (allBranches.length === 1 && String(allBranches[0]) === String(editMainBranch)) return '';
    
    return `[${allBranches.join(',')}]`;
  }, [editMainBranch]);

  // Convert selected branches array to JSON string format (reuse logic)
  const convertAddBranchesToJson = (branches) => {
    if (!branches || branches.length === 0) return '';
    if (addMainBranch === '0' || addMainBranch === 0) return '';
    const allBranches = branches.filter(id => id && String(id) !== '0');
    if (allBranches.length === 1 && String(allBranches[0]) === String(addMainBranch)) return '';
    return `[${allBranches.join(',')}]`;
  };

  // Memoized render function for selected branches
  const renderSelectedBranches = useMemo(() => (selected) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {selected.map((value) => {
        const isMainBranch = value === editMainBranch;
        const branchLabel = matchOptions(optionsBranch, value) || `สาขา ${value}`;
        const displayLabel = isMainBranch ? `${branchLabel} (หลัก)` : branchLabel;
        const branchColor = getBranchColor(value);
        
        return (
          <Chip 
            key={value} 
            label={displayLabel}
            size="small"
            sx={{
              backgroundColor: branchColor.bg,
              color: branchColor.color,
              fontWeight: isMainBranch ? 600 : 500,
              border: isMainBranch ? '2px solid' : 'none',
              borderColor: isMainBranch ? branchColor.color : 'transparent'
            }}
          />
        );
      })}
    </Box>
  ), [editMainBranch]);

  const handleSaveStaff = async () => {
    try {
      // Get values from refs instead of state
      const nameValue = nameRef.current?.value || '';
      const emailValue = emailRef.current?.value || '';
      const telValue = telRef.current?.value || '';
      const sfIdValue = sfIdRef.current?.value || '';
      const picUrlValue = picUrlRef.current?.value || '';
      
      // Check for leading spaces in ID before saving
      if (checkIdLeadingSpace(sfIdValue)) {
        alert('⚠️ ไม่สามารถบันทึกได้: ID ห้ามมีช่องว่างข้างหน้า');
        return;
      }
      
      // Convert selected branches to JSON format
      const formattedSetBId = convertBranchesToJson(selectedBranches);
      
      // Prepare data for API
      const staffData = {
        id: editingStaff.id, // Use id as primary key
        sf_id: sfIdValue, // This is the editable ID field from the form
        sf_name: nameValue,
        email: emailValue,
        tel: telValue,
        role: editRole,
        b_id: editMainBranch,
        set_b_id: formattedSetBId,
        pic_url: picUrlValue
      };
      
      console.log('Saving staff data:', staffData);
      
      // Call API to update staff data through crud.js
      const result = await updateStaff(staffData);
      
      if (result.success) {
        // Close modal and refresh data
        handleCloseEditModal();
        await getData(); // Refresh data
        alert('บันทึกข้อมูลสำเร็จ');
      } else {
        throw new Error(result.error || 'Failed to update staff');
      }
    } catch (error) {
      console.error('Error saving staff:', error);
      alert('เกิดข้อผิดพลาดในการบันทึกข้อมูล: ' + error.message);
    }
  };

  // Add Staff Save Handler
  const handleSaveAddStaff = async () => {
    try {
      const nameValue = addNameRef.current?.value || '';
      const emailValue = addEmailRef.current?.value || '';
      const telValue = addTelRef.current?.value || '';
      const sfIdValue = addSfIdRef.current?.value || '';
      const picUrlValue = addPicUrlRef.current?.value || '';
      
      // Check for leading spaces in ID before saving
      if (checkIdLeadingSpace(sfIdValue)) {
        alert('⚠️ ไม่สามารถบันทึกได้: ID ห้ามมีช่องว่างข้างหน้า');
        return;
      }
      
      const formattedSetBId = convertAddBranchesToJson(addSelectedBranches);
      const staffData = {
        id: sfIdValue, // Use the entered ID as the identifier
        sf_name: nameValue,
        email: emailValue,
        tel: telValue,
        role: addRole,
        b_id: addMainBranch,
        set_b_id: formattedSetBId,
        pic_url: picUrlValue
      };
      // Call API to add staff (assume addStaff exists in ../api/crud)
      const { addStaff } = await import('../api/crud');
      const result = await addStaff(staffData);
      if (result.success) {
        handleCloseAddModal();
        await getData();
        alert('เพิ่มบุคลากรสำเร็จ');
      } else {
        throw new Error(result.error || 'Failed to add staff');
      }
    } catch (error) {
      alert('เกิดข้อผิดพลาดในการเพิ่มบุคลากร: ' + error.message);
    }
  };

  // Function to get role label
  const getRoleLabel = (role) => {
    switch(role) {
      case "0": return "ผู้ดูแลระบบ/ประสานงาน";
      case "1": return "คณาจารย์นิเทศ";
      case "2": return "คณาจารย์นิเทศ (ผู้อนุมัติ)";
      default: return "ไม่ระบุ";
    }
  };

  // Memoized current role display to prevent recalculation on every render
  const getCurrentRoleDisplay = useMemo(() => {
    // Get main branch label
    const mainBranchLabel = editMainBranch === "0" || editMainBranch === 0
      ? 'ฝ่ายพัฒนานิสิต'
      : matchOptions(optionsBranch, editMainBranch) || `สาขา ${editMainBranch}`;
    
    // Special handling for ฝ่ายพัฒนานิสิต (b_id=0)
    const isStaffDevelopment = editMainBranch === "0" || editMainBranch === 0;
    
    // Get branch names for display - including main branch and additional branches
    let allBranchNames;
    if (isStaffDevelopment) {
      // For ฝ่ายพัฒนานิสิต, show that they can access all branches
      allBranchNames = 'เข้าถึงได้ทุกสาขา (ฝ่ายพัฒนานิสิต)';
    } else {
      allBranchNames = selectedBranches
        .map(id => {
          if (String(id) === String(editMainBranch)) {
            return `${mainBranchLabel} (หลัก)`;
          }
          return matchOptions(optionsBranch, id) || `สาขา ${id}`;
        })
        .filter(name => name)
        .join(', ');
    }
    
    // Get additional branch names (excluding main branch for display purposes)
    const additionalBranchNames = selectedBranches
      .filter(id => String(id) !== String(editMainBranch))
      .map(id => matchOptions(optionsBranch, id) || `สาขา ${id}`)
      .filter(name => name)
      .join(', ');
    
    return {
      roleLabel: getRoleLabel(editRole),
      mainBranchLabel,
      branchNames: allBranchNames || mainBranchLabel,
      additionalBranchNames: isStaffDevelopment ? 'ไม่ต้องกำหนด (เข้าถึงทุกสาขา)' : (additionalBranchNames || 'ไม่มี'),
      jsonPreview: convertBranchesToJson(selectedBranches)
    };
  }, [editRole, editMainBranch, selectedBranches, convertBranchesToJson]);

  const displayTableAdmin = (
    <Fade in={true} timeout={600}>
      <StyledCard>
        <GradientHeader sx={{ backgroundColor: '#e3f2fd' }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ backgroundColor: '#1976d2', color: 'white' }}>
              <AdminPanelSettingsIcon />
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 0.5, color: '#1976d2' }}>
                ฝ่ายพัฒนานิสิต
              </Typography>
              <Typography variant="body2" sx={{ color: '#1565c0' }}>
                <Chip 
                  label={`${adminList.length} คน`}
                  size="medium"
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    color: '#1976d2',
                    fontWeight: 'bold',
                    fontSize: '1rem',
                    mr: 1
                  }}
                />
                • อนุมัติได้ทุกสาขา
              </Typography>
            </Box>
          </Box>
        </GradientHeader>
        <StyledTableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ width: 80 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    ลำดับ
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <PersonIcon fontSize="small" />
                    ชื่อ-สกุล
                  </Box>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <BadgeIcon fontSize="small" />
                    บทบาท
                  </Box>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <SchoolIcon fontSize="small" />
                    สาขาวิชา
                  </Box>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <EmailIcon fontSize="small" />
                    อีเมล
                  </Box>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <PhoneIcon fontSize="small" />
                    เบอร์โทรศัพท์
                  </Box>
                </TableCell>
                <TableCell align="center">การดำเนินการ</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {adminList.map((row, index) => (
                <TableRow key={index}>
                  <TableCell align="center">
                    <Chip
                      label={index + 1}
                      size="small"
                      sx={{
                        backgroundColor: '#e3f2fd',
                        color: '#1976d2',
                        fontWeight: 'bold',
                        minWidth: 35
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar sx={{ backgroundColor: '#bbdefb', color: '#1976d2', width: 32, height: 32 }}>
                        <PersonIcon fontSize="small" />
                      </Avatar>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {row.sf_name || row.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label="ฝ่ายพัฒนานิสิต"
                      size="small"
                      sx={{
                        backgroundColor: '#e3f2fd',
                        color: '#1976d2',
                        fontWeight: 500
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        label={matchOptions(optionsBranch, row?.b_id)}
                        size="small"
                        sx={{
                          backgroundColor: getBranchColor(row?.b_id).bg,
                          color: getBranchColor(row?.b_id).color,
                          fontWeight: 500
                        }}
                      />
                      {parseSetBId(row?.set_b_id).filter(id => String(id) !== String(row?.b_id)).length > 0 && (
                        <Tooltip 
                          title={`สาขาเพิ่มเติม: ${getAdditionalBranchNames(row?.set_b_id, row?.b_id)}`}
                          arrow
                        >
                          <Chip
                            label={`+${parseSetBId(row?.set_b_id).filter(id => String(id) !== String(row?.b_id)).length}`}
                            size="small"
                            sx={{
                              backgroundColor: '#f5f5f5',
                              color: '#616161',
                              fontWeight: 500,
                              cursor: 'pointer'
                            }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {row.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {row.tel}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <ActionComponent id={row.id} staff={row} onEdit={handleEditStaff} onArchive={handleArchiveStaff} onUnarchive={handleUnarchiveStaff} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </StyledTableContainer>
      </StyledCard>
    </Fade>
  );
  const displayTableBranchSupervisor = (
    <Fade in={true} timeout={1000}>
      <StyledCard>
        <GradientHeader sx={{ backgroundColor: '#f3e5f5' }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ backgroundColor: '#9c27b0', color: 'white' }}>
              <SupervisorAccountIcon />
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 0.5, color: '#7b1fa2' }}>
                อาจารย์ผู้ประสานงานสาขา
              </Typography>
              <Typography variant="body2" sx={{ color: '#8e24aa' }}>
                <Chip 
                  label={`${supervisorBranchList.length} คน`}
                  size="medium"
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    color: '#7b1fa2',
                    fontWeight: 'bold',
                    fontSize: '1rem',
                    mr: 1
                  }}
                />
                • เรียงตามสาขา • อนุมัติได้เฉพาะสาขาของตน
              </Typography>
            </Box>
          </Box>
        </GradientHeader>
        <StyledTableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ width: 80 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    ลำดับ
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <PersonIcon fontSize="small" />
                    ชื่อ-สกุล
                  </Box>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <BadgeIcon fontSize="small" />
                    บทบาท
                  </Box>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <SchoolIcon fontSize="small" />
                    สาขาวิชา
                  </Box>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <EmailIcon fontSize="small" />
                    อีเมล
                  </Box>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <PhoneIcon fontSize="small" />
                    เบอร์โทรศัพท์
                  </Box>
                </TableCell>
                <TableCell align="center">การดำเนินการ</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {supervisorBranchList.map((row, index) => (
                <TableRow key={index}>
                  <TableCell align="center">
                    <Chip
                      label={index + 1}
                      size="small"
                      sx={{
                        backgroundColor: '#f3e5f5',
                        color: '#7b1fa2',
                        fontWeight: 'bold',
                        minWidth: 35
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar sx={{ backgroundColor: '#e1bee7', color: '#7b1fa2', width: 32, height: 32 }}>
                        <PersonIcon fontSize="small" />
                      </Avatar>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {row.sf_name || row.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label="ผู้ประสานงาน"
                      size="small"
                      sx={{
                        backgroundColor: '#f3e5f5',
                        color: '#7b1fa2',
                        fontWeight: 500
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        label={matchOptions(optionsBranch, row?.b_id)}
                        size="small"
                        sx={{
                          backgroundColor: getBranchColor(row?.b_id).bg,
                          color: getBranchColor(row?.b_id).color,
                          fontWeight: 500
                        }}
                      />
                      {parseSetBId(row?.set_b_id).filter(id => String(id) !== String(row?.b_id)).length > 0 && (
                        <Tooltip 
                          title={`สาขาเพิ่มเติม: ${getAdditionalBranchNames(row?.set_b_id, row?.b_id)}`}
                          arrow
                        >
                          <Chip
                            label={`+${parseSetBId(row?.set_b_id).filter(id => String(id) !== String(row?.b_id)).length}`}
                            size="small"
                            sx={{
                              backgroundColor: '#f5f5f5',
                              color: '#616161',
                              fontWeight: 500
                            }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {row.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {row.tel}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <ActionComponent id={row.id} staff={row} onEdit={handleEditStaff} onArchive={handleArchiveStaff} onUnarchive={handleUnarchiveStaff} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </StyledTableContainer>
      </StyledCard>
    </Fade>
  );
  const displayTableSupervisor = (
    <Fade in={true} timeout={1200}>
      <StyledCard>
        <GradientHeader sx={{ backgroundColor: '#e8f5e8' }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ backgroundColor: '#4caf50', color: 'white' }}>
                <SchoolIcon />
              </Avatar>
              <Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 0.5, color: '#2e7d32' }}>
                    คณาจารย์นิเทศ
                  </Typography>
                  {selectedBranch && (
                    <Chip 
                      size="small"
                      label={`กรองแล้ว: ${matchOptions(optionsBranch, selectedBranch)}`}
                      sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        color: '#2e7d32',
                        fontSize: '0.75rem'
                      }}
                    />
                  )}
                </Box>
                <Typography variant="body2" sx={{ color: '#388e3c' }}>
                  <Chip 
                    label={`${supervisorList.length} คน`}
                    size="medium"
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      color: '#2e7d32',
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      mr: 1
                    }}
                  />
                  • มีสิทธิ์อนุมัติ: 
                  <Chip 
                    label={`${supervisorList.filter(s => s.role === "2").length} คน`}
                    size="small"
                    sx={{
                      backgroundColor: 'rgba(25, 118, 210, 0.1)',
                      color: '#1976d2',
                      fontWeight: 'bold',
                      ml: 0.5
                    }}
                  />
                </Typography>
              </Box>
            </Box>
            <Box sx={{ minWidth: 200 }}>
              <FormControl fullWidth size="small">
                <InputLabel 
                  sx={{ 
                    color: '#2e7d32',
                    '&.Mui-focused': { color: '#2e7d32' }
                  }}
                >
                  กรองตามสาขา
                </InputLabel>
                <Select
                  value={selectedBranch}
                  onChange={handleBranchFilterChange}
                  label="กรองตามสาขา"
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    borderRadius: 2,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(46, 125, 50, 0.3)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2e7d32',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2e7d32',
                    }
                  }}
                >
                  <MenuItem value="">
                    <em>ทุกสาขา</em>
                  </MenuItem>
                  {optionsBranch.map((branch) => (
                    <MenuItem key={branch.value} value={String(branch.value)}>
                      {branch.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </GradientHeader>
        <StyledTableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ width: 80 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    ลำดับ
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <PersonIcon fontSize="small" />
                    ชื่อ-สกุล
                  </Box>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <BadgeIcon fontSize="small" />
                    บทบาท
                  </Box>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <SchoolIcon fontSize="small" />
                    สาขาวิชา
                  </Box>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <EmailIcon fontSize="small" />
                    อีเมล
                  </Box>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <PhoneIcon fontSize="small" />
                    เบอร์โทรศัพท์
                  </Box>
                </TableCell>
                <TableCell align="center">การดำเนินการ</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {supervisorList.map((row, index) => (
                <TableRow key={index}>
                  <TableCell align="center">
                    <Chip
                      label={index + 1}
                      size="small"
                      sx={{
                        backgroundColor: '#e8f5e8',
                        color: '#2e7d32',
                        fontWeight: 'bold',
                        minWidth: 35
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar sx={{ backgroundColor: '#c8e6c9', color: '#2e7d32', width: 32, height: 32 }}>
                        <PersonIcon fontSize="small" />
                      </Avatar>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {row.sf_name || row.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        label={row.role === "2" ? "คณาจารย์นิเทศ (ผู้อนุมัติ)" : "คณาจารย์นิเทศ"}
                        size="small"
                        sx={{
                          backgroundColor: row.role === "2" ? '#e3f2fd' : '#e8f5e8',
                          color: row.role === "2" ? '#1976d2' : '#2e7d32',
                          fontWeight: 500
                        }}
                      />
                      {row.role === "2" && (
                        <Tooltip title="มีสิทธิ์อนุมัติคำร้อง" arrow>
                          <AssignmentTurnedInIcon sx={{ color: '#1976d2', fontSize: 16 }} />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        label={matchOptions(optionsBranch, row?.b_id)}
                        size="small"
                        sx={{
                          backgroundColor: getBranchColor(row?.b_id).bg,
                          color: getBranchColor(row?.b_id).color,
                          fontWeight: 500
                        }}
                      />
                      {parseSetBId(row?.set_b_id).filter(id => String(id) !== String(row?.b_id)).length > 0 && (
                        <Tooltip 
                          title={`สาขาเพิ่มเติม: ${getAdditionalBranchNames(row?.set_b_id, row?.b_id)}`}
                          arrow
                        >
                          <Chip
                            label={`+${parseSetBId(row?.set_b_id).filter(id => String(id) !== String(row?.b_id)).length}`}
                            size="small"
                            sx={{
                              backgroundColor: '#f5f5f5',
                              color: '#616161',
                              fontWeight: 500
                            }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {row.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {row.tel}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <ActionComponent id={row.id} staff={row} onEdit={handleEditStaff} onArchive={handleArchiveStaff} onUnarchive={handleUnarchiveStaff} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </StyledTableContainer>
      </StyledCard>
    </Fade>
  );
  const displayTableArchived = (
    <Fade in={true} timeout={1400}>
      <StyledCard>
        <GradientHeader sx={{ backgroundColor: '#ffebee' }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ backgroundColor: '#f44336', color: 'white' }}>
              <ArchiveIcon />
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 0.5, color: '#c62828' }}>
                เจ้าหน้าที่ที่เก็บเข้าคลัง
              </Typography>
              <Typography variant="body2" sx={{ color: '#d32f2f' }}>
                <Chip 
                  label={`${archivedStaffList.length} คน`}
                  size="medium"
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    color: '#c62828',
                    fontWeight: 'bold',
                    fontSize: '1rem',
                    mr: 1
                  }}
                />
                • ไม่สามารถเข้าใช้งานระบบได้ • เก็บไว้เพื่อการอ้างอิง
              </Typography>
            </Box>
          </Box>
        </GradientHeader>
        <StyledTableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ width: 80 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    ลำดับ
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <PersonIcon fontSize="small" />
                    ชื่อ-สกุล
                  </Box>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <BadgeIcon fontSize="small" />
                    สถานะ
                  </Box>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <SchoolIcon fontSize="small" />
                    สาขาวิชา
                  </Box>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <EmailIcon fontSize="small" />
                    อีเมล
                  </Box>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <PhoneIcon fontSize="small" />
                    เบอร์โทรศัพท์
                  </Box>
                </TableCell>
                <TableCell align="center">การดำเนินการ</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {archivedStaffList.map((row, index) => (
                <TableRow 
                  key={index}
                  sx={{
                    backgroundColor: 'rgba(244, 67, 54, 0.05)',
                    '&:hover': {
                      backgroundColor: 'rgba(244, 67, 54, 0.1)',
                    }
                  }}
                >
                  <TableCell align="center">
                    <Chip
                      label={index + 1}
                      size="small"
                      sx={{
                        backgroundColor: '#ffebee',
                        color: '#c62828',
                        fontWeight: 'bold',
                        minWidth: 35
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar sx={{ backgroundColor: '#ffcdd2', color: '#c62828', width: 32, height: 32 }}>
                        <PersonIcon fontSize="small" />
                      </Avatar>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body1" sx={{ fontWeight: 500, color: '#757575' }}>
                          {row.sf_name || row.name}
                        </Typography>
                        <Chip
                          label="เก็บเข้าคลัง"
                          size="small"
                          sx={{
                            backgroundColor: '#ffebee',
                            color: '#c62828',
                            fontSize: '0.7rem'
                          }}
                        />
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label="ไม่ใช้งาน"
                      size="small"
                      sx={{
                        backgroundColor: '#ffebee',
                        color: '#c62828',
                        fontWeight: 500
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        label={matchOptions(optionsBranch, row?.b_id)}
                        size="small"
                        sx={{
                          backgroundColor: getBranchColor(row?.b_id).bg,
                          color: getBranchColor(row?.b_id).color,
                          fontWeight: 500,
                          opacity: 0.7
                        }}
                      />
                      {parseSetBId(row?.set_b_id).filter(id => String(id) !== String(row?.b_id)).length > 0 && (
                        <Tooltip 
                          title={`สาขาเพิ่มเติม: ${getAdditionalBranchNames(row?.set_b_id, row?.b_id)}`}
                          arrow
                        >
                          <Chip
                            label={`+${parseSetBId(row?.set_b_id).filter(id => String(id) !== String(row?.b_id)).length}`}
                            size="small"
                            sx={{
                              backgroundColor: '#f5f5f5',
                              color: '#616161',
                              fontWeight: 500,
                              cursor: 'pointer',
                              opacity: 0.7
                            }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.disabled">
                      {row.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.disabled">
                      {row.tel}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <ActionComponent 
                      id={row.id} 
                      staff={row} 
                      onEdit={handleEditStaff}
                      onArchive={handleArchiveStaff}
                      onUnarchive={handleUnarchiveStaff}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {archivedStaffList.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                      <ArchiveIcon sx={{ fontSize: 48, color: '#bdbdbd' }} />
                      <Typography variant="body1" color="text.secondary">
                        ไม่มีเจ้าหน้าที่ที่เก็บเข้าคลัง
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </StyledTableContainer>
      </StyledCard>
    </Fade>
  );
  
  
 
  
  // Optimized Edit Staff Modal Component with uncontrolled inputs
  const EditStaffModal = React.memo(() => (
    <Dialog
      open={editModalOpen}
      onClose={handleCloseEditModal}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ 
        backgroundColor: '#f8f9fa', 
        color: '#495057',
        borderBottom: '1px solid #dee2e6'
      }}>
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar sx={{ backgroundColor: '#4a90e2', color: 'white' }}>
            <EditIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              แก้ไขข้อมูลบุคลากร
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {editingStaff?.sf_name || editingStaff?.name}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {/* Basic Information */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, color: '#495057', fontWeight: 600 }}>
              ข้อมูลทั่วไป
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="รหัสบุคลากร (ID)"
                  defaultValue={editingStaff?.id || ''}
                  inputRef={sfIdRef}
                  variant="outlined"
                  InputProps={{
                    style: { backgroundColor: '#ffffff' },
                    endAdornment: editIdHasLeadingSpace && (
                      <Tooltip title="พบช่องว่างข้างหน้า ID" arrow>
                        <WarningIcon sx={{ color: '#f44336', mr: 1 }} />
                      </Tooltip>
                    )
                  }}
                  error={editIdHasLeadingSpace}
                  helperText={editIdHasLeadingSpace ? '⚠️ ห้ามมีช่องว่างนำหน้า ID' : ''}
                  onChange={handleEditIdChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="URL รูปภาพ (pic_url)"
                  defaultValue={editingStaff?.pic_url || ''}
                  inputRef={picUrlRef}
                  variant="outlined"
                  InputProps={{
                    style: { backgroundColor: '#ffffff' }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="ชื่อ-สกุล"
                  defaultValue={editingStaff?.sf_name || editingStaff?.name || ''}
                  inputRef={nameRef}
                  variant="outlined"
                  InputProps={{
                    style: { backgroundColor: '#ffffff' }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="อีเมล"
                  type="email"
                  defaultValue={editingStaff?.email || ''}
                  inputRef={emailRef}
                  variant="outlined"
                  InputProps={{
                    style: { backgroundColor: '#ffffff' }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="เบอร์โทรศัพท์"
                  defaultValue={editingStaff?.tel || ''}
                  inputRef={telRef}
                  variant="outlined"
                  InputProps={{
                    style: { backgroundColor: '#ffffff' }
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* Role and Branch Information */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, color: '#495057', fontWeight: 600 }}>
              บทบาทและสาขา
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>บทบาท</InputLabel>
                  <Select
                    value={editRole}
                    onChange={handleRoleChange}
                    label="บทบาท"
                  >
                    <MenuItem value="0">ผู้ดูแลระบบ/ประสานงาน</MenuItem>
                    <MenuItem value="1">คณาจารย์นิเทศ</MenuItem>
                    <MenuItem value="2">คณาจารย์นิเทศ (ผู้อนุมัติ)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>สาขาหลัก</InputLabel>
                  <Select
                    value={editMainBranch}
                    onChange={handleMainBranchChange}
                    label="สาขาหลัก"
                  >
                    <MenuItem value="0">ฝ่ายพัฒนานิสิต</MenuItem>
                    {optionsBranch.map((branch) => (
                      <MenuItem key={branch.value} value={String(branch.value)}>
                        {branch.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>สาขาเพิ่มเติมที่เข้าถึงได้ (นอกเหนือจากสาขาหลัก)</InputLabel>
                  <Select
                    multiple
                    value={selectedBranches}
                    onChange={handleBranchSelectionChange}
                    input={<OutlinedInput label="สาขาเพิ่มเติมที่เข้าถึงได้ (นอกเหนือจากสาขาหลัก)" />}
                    renderValue={renderSelectedBranches}
                  >
                    {optionsBranch.map((branch) => (
                      <MenuItem key={branch.value} value={String(branch.value)}>
                        <Checkbox checked={selectedBranches.indexOf(String(branch.value)) > -1} />
                        <ListItemText primary={branch.label} />
                      </MenuItem>
                    ))}
                  </Select>
                  <Box sx={{ mt: 1, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1, border: '1px solid #e9ecef' }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#495057' }}>
                      💡 คำอธิบายการกำหนดสาขา:
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5, color: '#6c757d', fontSize: '0.75rem' }}>
                      • <strong>สาขาหลัก (b_id)</strong>: สาขาที่บุคลากรสังกัด และมีสิทธิ์เข้าถึงโดยอัตโนมัติ
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5, color: '#6c757d', fontSize: '0.75rem' }}>
                      • <strong>สาขาที่เข้าถึงได้ (set_b_id)</strong>: รวมสาขาหลัก + สาขาเพิ่มเติมทั้งหมดที่เข้าถึงได้
                    </Typography>
                    <Box sx={{ backgroundColor: '#fff3cd', borderRadius: 1, p: 1, mt: 1, border: '1px solid #ffeaa7' }}>
                      <Typography variant="body2" sx={{ mb: 0.5, color: '#856404', fontSize: '0.75rem', fontWeight: 600 }}>
                        🔄 กรณีพิเศษ Role = 0:
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5, color: '#856404', fontSize: '0.75rem' }}>
                        • <strong>ฝ่ายพัฒนานิสิต</strong> (b_id=0): เข้าถึงทุกสาขา ไม่ต้องกำหนด set_b_id
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#856404', fontSize: '0.75rem' }}>
                        • <strong>อ.ผู้ประสานงาน</strong> (b_id=1-7): set_b_id จะรวมสาขาหลัก + สาขาเพิ่มเติม เช่น [4,7]
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: '#28a745', fontSize: '0.75rem', fontWeight: 500, mt: 1 }}>
                      ✅ สาขาหลักจะถูกเพิ่มอัตโนมัติ และแสดงเป็นสาขาแรกพร้อมเครื่องหมาย "(หลัก)"
                    </Typography>
                  </Box>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          {/* Current Role Display */}
          <Box sx={{ 
            backgroundColor: '#f8f9fa', 
            borderRadius: 2, 
            p: 2,
            border: '1px solid #dee2e6'
          }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#495057' }}>
              📋 ตัวอย่างการบันทึกข้อมูล:
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5, color: '#6c757d' }}>
              <strong>บทบาท:</strong> {getCurrentRoleDisplay.roleLabel || 'ไม่ระบุ'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5, color: '#6c757d' }}>
              <strong>สาขาหลัก (b_id):</strong> {getCurrentRoleDisplay.mainBranchLabel || 'ไม่ระบุ'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5, color: '#6c757d' }}>
              <strong>สาขาเพิ่มเติม:</strong> {getCurrentRoleDisplay.additionalBranchNames || 'ไม่มี'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5, color: '#6c757d' }}>
              <strong>สาขาทั้งหมดที่เข้าถึงได้:</strong> {getCurrentRoleDisplay.branchNames || getCurrentRoleDisplay.mainBranchLabel || 'ไม่ระบุ'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, color: '#495057', fontWeight: 500 }}>
              <strong>สาขาที่บันทึกใน set_b_id:</strong> {
                getCurrentRoleDisplay.jsonPreview
                  ? getCurrentRoleDisplay.jsonPreview
                  : (editMainBranch === "0" || editMainBranch === 0
                      ? 'ไม่มี (ฝ่ายพัฒนานิสิต)'
                      : 'เข้าได้เฉพาะสาขาหลัก')
              }
            </Typography>
            <Box sx={{ backgroundColor: '#e9ecef', borderRadius: 1, p: 1, border: '1px solid #ced4da' }}>
              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#495057', fontFamily: 'monospace' }}>
                <strong>JSON ที่จะส่ง:</strong> role={editRole || '0'}, b_id={editMainBranch || '0'}, set_b_id="{getCurrentRoleDisplay.jsonPreview || ''}"
              </Typography>
            </Box>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
        <Button
          onClick={handleCloseEditModal}
          variant="outlined"
          startIcon={<CancelIcon />}
          sx={{ 
            borderColor: '#6c757d',
            color: '#6c757d',
            '&:hover': {
              borderColor: '#5a6268',
              backgroundColor: 'rgba(108, 117, 125, 0.1)'
            }
          }}
        >
          ยกเลิก
        </Button>
        <Button
          onClick={handleSaveStaff}
          variant="contained"
          startIcon={<SaveIcon />}
          sx={{
            backgroundColor: '#4a90e2',
            '&:hover': {
              backgroundColor: '#357abd'
            }
          }}
        >
          บันทึก
        </Button>
      </DialogActions>
    </Dialog>
  ));
  
  // AddStaffModal Component
  const AddStaffModal = React.memo(() => (
    <Dialog
      open={addModalOpen}
      onClose={handleCloseAddModal}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ backgroundColor: '#e3f2fd', color: '#1976d2', borderBottom: '1px solid #dee2e6' }}>
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar sx={{ backgroundColor: '#1976d2', color: 'white' }}>
            <PersonIcon />
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>เพิ่มบุคลากรใหม่</Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Box>
            <Typography variant="h6" sx={{ mb: 2, color: '#495057', fontWeight: 600 }}>ข้อมูลทั่วไป</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField 
                  fullWidth 
                  label="รหัสบุคลากร (ID)" 
                  inputRef={addSfIdRef} 
                  variant="outlined" 
                  InputProps={{ 
                    style: { backgroundColor: '#ffffff' },
                    endAdornment: addIdHasLeadingSpace && (
                      <Tooltip title="พบช่องว่างข้างหน้า ID" arrow>
                        <WarningIcon sx={{ color: '#f44336', mr: 1 }} />
                      </Tooltip>
                    )
                  }} 
                  error={addIdHasLeadingSpace} 
                  helperText={addIdHasLeadingSpace ? '⚠️ ห้ามมีช่องว่างนำหน้า ID' : ''} 
                  onChange={handleAddIdChange} 
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="URL รูปภาพ (pic_url)" inputRef={addPicUrlRef} variant="outlined" InputProps={{ style: { backgroundColor: '#ffffff' } }} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="ชื่อ-สกุล" inputRef={addNameRef} variant="outlined" InputProps={{ style: { backgroundColor: '#ffffff' } }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="อีเมล" type="email" inputRef={addEmailRef} variant="outlined" InputProps={{ style: { backgroundColor: '#ffffff' } }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="เบอร์โทรศัพท์" inputRef={addTelRef} variant="outlined" InputProps={{ style: { backgroundColor: '#ffffff' } }} />
              </Grid>
            </Grid>
          </Box>
          <Divider />
          <Box>
            <Typography variant="h6" sx={{ mb: 2, color: '#495057', fontWeight: 600 }}>บทบาทและสาขา</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>บทบาท</InputLabel>
                  <Select
                    value={addRole}
                    onChange={handleAddRoleChange}
                    label="บทบาท"
                  >
                    <MenuItem value="0">ผู้ดูแลระบบ/ประสานงาน</MenuItem>
                    <MenuItem value="1">คณาจารย์นิเทศ</MenuItem>
                    <MenuItem value="2">คณาจารย์นิเทศ (ผู้อนุมัติ)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>สาขาหลัก</InputLabel>
                  <Select
                    value={addMainBranch}
                    onChange={handleAddMainBranchChange}
                    label="สาขาหลัก"
                  >
                    <MenuItem value="0">ฝ่ายพัฒนานิสิต</MenuItem>
                    {optionsBranch.map((branch) => (
                      <MenuItem key={branch.value} value={String(branch.value)}>
                        {branch.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>สาขาเพิ่มเติมที่เข้าถึงได้ (นอกเหนือจากสาขาหลัก)</InputLabel>
                  <Select
                    multiple
                    value={addSelectedBranches}
                    onChange={handleAddBranchSelectionChange}
                    input={<OutlinedInput label="สาขาเพิ่มเติมที่เข้าถึงได้ (นอกเหนือจากสาขาหลัก)" />}
                    renderValue={renderSelectedBranches}
                  >
                    {optionsBranch.map((branch) => (
                      <MenuItem key={branch.value} value={String(branch.value)}>
                        <Checkbox checked={addSelectedBranches.indexOf(String(branch.value)) > -1} />
                        <ListItemText primary={branch.label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
        <Button onClick={handleCloseAddModal} variant="outlined" startIcon={<CancelIcon />} sx={{ borderColor: '#6c757d', color: '#6c757d', '&:hover': { borderColor: '#5a6268', backgroundColor: 'rgba(108, 117, 125, 0.1)' } }}>ยกเลิก</Button>
        <Button onClick={handleSaveAddStaff} variant="contained" startIcon={<SaveIcon />} sx={{ backgroundColor: '#1976d2', '&:hover': { backgroundColor: '#1565c0' } }}>บันทึก</Button>
      </DialogActions>
    </Dialog>
  ));



  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Fade in={true} timeout={600}>
        <Box>
          {/* Header Section */}
          <Box 
            mb={4} 
            sx={{
              backgroundColor: '#f8f9fa',
              borderRadius: 3,
              p: 4,
              color: '#495057',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
              border: '1px solid #e9ecef'
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography 
                variant="h3" 
                component="h1" 
                sx={{ 
                  fontWeight: 'bold',
                  fontSize: { xs: '1.8rem', md: '2.5rem' },
                  mb: 1,
                  color: '#343a40'
                }}
              >
                การจัดการบุคลากร
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 400,
                  fontSize: { xs: '1rem', md: '1.25rem' },
                  mb: 2,
                  color: '#6c757d'
                }}
              >
                จัดการข้อมูลฝ่ายพัฒนานิสิต ผู้ประสานงาน และคณาจารย์นิเทศ
              </Typography>
              {/* Add Staff Button */}
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                sx={{ mb: 2, fontWeight: 600, backgroundColor: '#1976d2', '&:hover': { backgroundColor: '#1565c0' } }}
                onClick={handleOpenAddModal}
              >
                เพิ่มบุคลากร
              </Button>
              {/* หมายเหตุสิทธิ์การเข้าถึง */}
              <Box 
                sx={{ 
                  backgroundColor: '#fff',
                  borderRadius: 2,
                  p: 2,
                  border: '1px solid #dee2e6',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                }}
              >
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#495057' }}>
                  🔐 โครงสร้างสิทธิ์การเข้าถึงและอนุมัติ:
                </Typography>
                <Box sx={{ pl: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1, color: '#dc3545', fontWeight: 600 }}>
                    <strong>บทบาท Role = 0 (ผู้ดูแลระบบ/ประสานงาน):</strong>
                  </Typography>
                  <Box sx={{ pl: 2, mb: 1 }}>
                    <Typography variant="body2" sx={{ mb: 0.5, color: '#6c757d' }}>
                      • <strong>ฝ่ายพัฒนานิสิต</strong> (b_id=0): จัดการได้ทุกหน้าทุกอย่าง ทุกสาขา (Super Admin)
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5, color: '#6c757d' }}>
                      • <strong>อ.ผู้ประสานงานสาขา</strong> (b_id=1-7): อนุมัติได้เฉพาะสาขาของตน + สาขาเพิ่มเติม
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ mb: 0.5, color: '#6c757d' }}>
                    • <strong>คณาจารย์นิเทศ</strong> (role=1, b_id=1-7): นิเทศนิสิต ไม่มีสิทธิ์อนุมัติคำร้อง
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1, color: '#6c757d' }}>
                    • <strong>คณาจารย์นิเทศ + สิทธิ์อนุมัติ</strong> (role=2, b_id=1-7): นิเทศนิสิต พร้อมสิทธิ์อนุมัติคำร้อง (แสดงไอคอน 📋)
                  </Typography>
                  <Box sx={{ backgroundColor: '#f8f9fa', borderRadius: 1, p: 1.5, mt: 1, border: '1px solid #e9ecef' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#495057', mb: 0.5 }}>
                      💡 เกี่ยวกับ set_b_id:
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6c757d', fontSize: '0.85rem' }}>
                      • <strong>ฝ่ายพัฒนานิสิต</strong> (b_id=0): ไม่ต้องกำหนด set_b_id เพราะเข้าถึงทุกสาขาอยู่แล้ว
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6c757d', fontSize: '0.85rem' }}>
                      • <strong>บุคลากรสาขาอื่น</strong> (b_id=1-7): set_b_id จะรวมสาขาหลัก + สาขาเพิ่มเติม เช่น [4,7]
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#28a745', fontSize: '0.75rem', fontWeight: 500, mt: 1 }}>
                    ✅ สาขาหลักจะถูกเพิ่มอัตโนมัติ และแสดงเป็นสาขาแรกพร้อมเครื่องหมาย "(หลัก)"
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Content Section */}
          <Grid container spacing={4}>
            <Grid item xs={12}>
              {displayTableAdmin}
            </Grid>
            <Grid item xs={12}>
              {displayTableBranchSupervisor}
            </Grid>
            <Grid item xs={12}>
              {displayTableSupervisor}
            </Grid>
            <Grid item xs={12}>
              {displayTableArchived}
            </Grid>
          </Grid>
        </Box>
      </Fade>
      {/* Add Staff Modal */}
      <AddStaffModal />
      {/* Edit Staff Modal */}
      <EditStaffModal key={editingStaff?.id || 'new'} />
    </Container>
  );
};

export default Staff;


